<template>
  <div class="profile-page">
    <section class="page-hero">
      <h1>个人中心</h1>
      <p>管理你的账号，和一路积累下来的阅读与聆听足迹。</p>
    </section>

    <main class="profile-body">
      <!-- 概览卡 -->
      <section class="card overview">
        <div class="avatar">{{ (username || '?').charAt(0) }}</div>
        <div class="ov-info">
          <div class="ov-name">{{ username }}</div>
          <div class="ov-sub">欢迎回到你的书房</div>
        </div>
      </section>

      <!-- 数据统计 -->
      <section class="stats">
        <button class="stat" @click="goMine">
          <nricon name="collection" />
          <div class="s-num">{{ uploadCount }}</div>
          <div class="s-label">我的上传</div>
        </button>
        <button class="stat" @click="goNotes">
          <nricon name="notebook-1" />
          <div class="s-num">{{ noteCount }}</div>
          <div class="s-label">笔记数</div>
        </button>
        <div class="stat stat--static">
          <nricon name="time" />
          <div class="s-num">{{ readTimeText }}</div>
          <div class="s-label">累计阅读</div>
        </div>
        <div class="stat stat--static">
          <nricon name="headphones" />
          <div class="s-num">{{ listenTimeText }}</div>
          <div class="s-label">累计聆听</div>
        </div>
      </section>

      <section class="card appearance-card">
        <div class="card-heading"><div><div class="card-title">应用外观</div><p>跟随系统，或固定使用浅色与深色。</p></div><nricon name="sunny" /></div>
        <div class="appearance-options" role="group" aria-label="应用外观">
          <button v-for="option in themeOptions" :key="option.value" type="button" :class="{ active: appTheme === option.value }" :aria-pressed="appTheme === option.value" @click="setAppTheme(option.value)">
            <nricon :name="option.icon" :size="17" />{{ option.label }}
          </button>
        </div>
      </section>

      <!-- 修改密码 -->
      <section class="card">
        <div class="card-heading"><div><div class="card-title">修改密码</div></div><nricon name="lock" /></div>
        <div class="form">
          <div class="field">
            <label>原密码</label>
            <input type="password" class="nr-input" v-model="pwd.oldPassword" placeholder="请输入原密码" autocomplete="current-password">
          </div>
          <div class="field">
            <label>新密码</label>
            <input type="password" class="nr-input" v-model="pwd.newPassword" placeholder="至少 6 位" autocomplete="new-password">
          </div>
          <div class="field">
            <label>确认新密码</label>
            <input type="password" class="nr-input" v-model="pwd.confirm" placeholder="再次输入新密码" autocomplete="new-password">
          </div>
          <div v-if="pwdError" class="form-error">{{ pwdError }}</div>
          <div class="form-actions">
            <button class="btn btn--primary btn--sm" :disabled="saving" @click="changePassword">
              <nricon v-if="saving" name="loading" />
              <span v-else>保存修改</span>
            </button>
          </div>
        </div>
      </section>

      <!-- 快捷入口 -->
      <section class="card links">
        <button class="link-row" @click="goNotes"><nricon name="notebook-1" /> 我的笔记 <nricon class="go" name="arrow-right" /></button>
        <button class="link-row" @click="goMine"><nricon name="reading" /> 我的书库 <nricon class="go" name="arrow-right" /></button>
        <button class="link-row danger" @click="logout"><nricon name="switch-button" /> 退出登录</button>
      </section>
    </main>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'ProfilePage',
  data() {
    return {
      uploadCount: 0,
      noteCount: 0,
      totalReadSeconds: 0,
      totalListeningSeconds: 0,
      pwd: { oldPassword: '', newPassword: '', confirm: '' },
      pwdError: '',
      saving: false,
      themeOptions: [
        { value: 'system', label: '跟随系统', icon: 'setting' },
        { value: 'light', label: '浅色', icon: 'sunny' },
        { value: 'dark', label: '深色', icon: 'reading' }
      ]
    }
  },
  computed: {
    username() {
      return this.$store.state.user && this.$store.state.user.username
    },
    appTheme() { return this.$store.state.theme || 'system' },
    readTimeText() { return this.formatTotalDuration(this.totalReadSeconds) },
    listenTimeText() { return this.formatTotalDuration(this.totalListeningSeconds) }
  },
  created() {
    if (!this.$store.state.user) {
      this.$router.replace('/login')
      return
    }
    this.loadStats()
  },
  methods: {
    formatTotalDuration(seconds) {
      const value = Number(seconds) || 0
      const hours = Math.floor(value / 3600)
      const minutes = Math.floor((value % 3600) / 60)
      if (hours > 0) return `${hours}h${minutes}m`
      if (minutes > 0) return `${minutes} 分`
      return '—'
    },
    async loadStats() {
      try {
        const { data } = await api.book.getBooks({ scope: 'mine', page: 1, pageSize: 1 })
        this.uploadCount = Number(data && data.total) || 0
      } catch (e) { /* ignore */ }
      try {
        const { data } = await api.stats.total()
        this.totalReadSeconds = data.readingSeconds != null ? data.readingSeconds : data.totalSeconds || 0
        this.totalListeningSeconds = data.listeningSeconds || 0
      } catch (e) { /* ignore */ }
      try {
        const { data } = await api.bookmark.listAll()
        this.noteCount = (data || []).length
      } catch (e) { /* ignore */ }
    },
    async changePassword() {
      this.pwdError = ''
      if (!this.pwd.oldPassword) { this.pwdError = '请输入原密码'; return }
      if (!this.pwd.newPassword || this.pwd.newPassword.length < 6) { this.pwdError = '新密码至少 6 位'; return }
      if (this.pwd.newPassword !== this.pwd.confirm) { this.pwdError = '两次输入的新密码不一致'; return }
      this.saving = true
      try {
        await api.user.changePassword({
          oldPassword: this.pwd.oldPassword,
          newPassword: this.pwd.newPassword
        })
        this.$message.success('密码修改成功')
        this.pwd = { oldPassword: '', newPassword: '', confirm: '' }
      } catch (e) {
        this.pwdError = (e.response && e.response.data && e.response.data.message) || '修改失败'
      } finally {
        this.saving = false
      }
    },
    goNotes() { this.$router.push('/notes') },
    goMine() { this.$router.push('/library') },
    setAppTheme(value) { this.$store.commit('setTheme', value) },
    logout() {
      this.$store.commit('logout')
      this.$router.push('/')
      this.$message.success('已退出登录')
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-page {
  min-height: 100vh;
  background: var(--nr-bg);
  padding-bottom: 40px;
}

.page-top {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: var(--nr-surface);
  border-bottom: 1px solid var(--nr-border);

  h2 { margin: 0; font-size: 17px; font-weight: 500; color: var(--nr-text); }
  .spacer { flex: 1; }
  .back { flex-shrink: 0; }
}

.profile-body {
  max-width: 640px;
  margin: 0 auto;
  padding: 18px 20px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card {
  background: var(--nr-surface);
  border: 1px solid var(--nr-border);
  border-radius: var(--nr-radius-lg);
  padding: 18px;
}

.overview {
  display: flex;
  align-items: center;
  gap: 16px;

  .avatar {
    width: 56px; height: 56px; border-radius: 50%;
    background: var(--nr-accent-bg); color: var(--nr-accent-text);
    display: flex; align-items: center; justify-content: center;
    font-size: 24px; font-weight: 500; flex-shrink: 0;
  }
  .ov-name { font-size: 18px; font-weight: 500; color: var(--nr-text); }
  .ov-sub { font-size: 13px; color: var(--nr-text-3); margin-top: 2px; }
}

.stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  .stat {
    background: var(--nr-surface);
    border: 1px solid var(--nr-border);
    border-radius: var(--nr-radius);
    padding: 16px 10px;
    text-align: center;
    cursor: pointer;
    &:hover { box-shadow: var(--nr-shadow-hover); }
    .s-num { font-size: 20px; font-weight: 600; color: var(--nr-text); }
    .s-label { font-size: 12px; color: var(--nr-text-3); margin-top: 4px; }
  }
}

.card-title {
  font-size: 15px;
  font-weight: 500;
  color: var(--nr-text);
  margin-bottom: 14px;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    label { font-size: 13px; color: var(--nr-text-2); }
  }
  .form-error { color: var(--nr-danger); font-size: 13px; }
  .form-actions { display: flex; justify-content: flex-end; }
}

.links {
  padding: 6px;
  display: flex;
  flex-direction: column;

  .link-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 12px;
    border: none;
    background: transparent;
    color: var(--nr-text);
    font-size: 14px;
    text-align: left;
    border-radius: var(--nr-radius);
    cursor: pointer;
    &:hover { background: var(--nr-surface-2); }
    .go { margin-left: auto; color: var(--nr-text-3); font-size: 13px; }
    &.danger { color: var(--nr-danger); }
  }
}

@media screen and (max-width: 768px) {
  .page-top { padding: 10px 14px; }
  .profile-body { padding: 14px 14px 0; }
}
</style>

<style lang="scss" scoped>
.profile-page { padding-bottom: 72px; background: transparent; }

.page-top {
  padding: 0;
  border-bottom: 1px solid color-mix(in srgb, var(--nr-border) 80%, transparent);
  background: color-mix(in srgb, var(--nr-surface) 90%, transparent);
  backdrop-filter: blur(18px);
}
.page-top-inner {
  display: flex;
  width: min(100% - 40px, 860px);
  min-height: 68px;
  align-items: center;
  justify-content: space-between;
  margin-inline: auto;
}
.back,
.mini-brand {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 7px;
  padding: 0;
  color: var(--nr-text-2);
  border: 0;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.back:hover { color: var(--nr-accent-text); }
.mini-brand { color: var(--nr-text); font-family: var(--nr-font-display); font-size: 18px; }
.mini-brand i { display: grid; width: 31px; height: 31px; place-items: center; color: var(--nr-on-accent); border-radius: 50% 50% 50% 10px; background: var(--nr-accent); font-family: var(--nr-font-body); font-size: 16px; }

.page-hero {
  width: min(100% - 40px, 860px);
  margin-inline: auto;
  padding: 34px 0 22px;
  border-bottom: 1px solid var(--nr-border);
  h1 { margin: 0; font-family: var(--nr-font-display); font-size: clamp(30px, 4vw, 38px); font-weight: 600; line-height: 1.15; letter-spacing: -.02em; }
  p { margin: 10px 0 0; color: var(--nr-text-2); font-size: 14px; }
}

.profile-body { max-width: 860px; padding: 30px 0 0; gap: 18px; }

.card {
  padding: 26px;
  border-radius: var(--nr-radius-lg);
  box-shadow: none;
}

.overview {
  min-height: 0;
  gap: 18px;
  overflow: hidden;
  color: var(--nr-text);
  border: 1px solid var(--nr-border);
  background: var(--nr-surface);

  .avatar { width: 60px; height: 60px; color: var(--nr-accent-text); border: 0; background: var(--nr-accent-bg); font-size: 26px; font-weight: 600; }
  .ov-name { color: var(--nr-text); font-size: 20px; font-weight: 600; }
  .ov-sub { color: var(--nr-text-2); }
}

.stats { gap: 13px; }
.stats .stat {
  display: grid;
  min-height: 116px;
  place-items: center;
  align-content: center;
  gap: 2px;
  padding: 16px 10px;
  color: inherit;
  border-radius: var(--nr-radius);
  background: var(--nr-surface);
  font: inherit;
  transition: border-color 180ms ease, box-shadow 180ms ease;

  > i { margin-bottom: 7px; color: var(--nr-accent-text); font-size: 18px; }
  .s-num { font-size: 22px; font-weight: 700; }
  .s-label { color: var(--nr-text-3); font-size: 11px; }
  &:not(.stat--static):hover { border-color: var(--nr-border-strong); box-shadow: var(--nr-shadow); }
  &.stat--static { cursor: default; }
}

.card-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--nr-border);

  > i { display: grid; width: 38px; height: 38px; place-items: center; color: var(--nr-accent-text); border-radius: 50%; background: var(--nr-accent-bg); }
  .card-title { margin: 0; font-size: 16px; font-weight: 700; }
}

.appearance-card .card-heading { margin-bottom: 13px; }
.appearance-card .card-heading p { margin: 3px 0 0; color: var(--nr-text-3); font-size: 12px; }
.appearance-options { display: flex; flex-wrap: wrap; gap: 8px; }
.appearance-options button { display: inline-flex; min-height: 40px; align-items: center; gap: 7px; padding: 0 12px; color: var(--nr-text-2); border: 1px solid var(--nr-border); border-radius: 9px; background: var(--nr-surface); font-size: 12px; cursor: pointer; transition: color 160ms ease, border-color 160ms ease, background 160ms ease; &:hover { border-color: var(--nr-accent); } &.active { color: var(--nr-accent-text); border-color: var(--nr-accent); background: var(--nr-accent-bg); font-weight: 650; } }

.form { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.form .field { gap: 7px; }
.form .field label { font-size: 12px; font-weight: 600; }
.form .form-error,
.form .form-actions { grid-column: 1 / -1; }
.form .form-error { color: var(--nr-danger); }
.form .form-actions { margin-top: 4px; }

.links { padding: 8px; }
.links .link-row {
  min-height: 50px;
  padding: 10px 13px;
  border-radius: 10px;
  font-weight: 500;
  transition: background 180ms ease, color 180ms ease;
  > i:first-child { display: grid; width: 30px; height: 30px; place-items: center; color: var(--nr-accent-text); border-radius: 50%; background: var(--nr-accent-bg); }
  &.danger > i:first-child { color: var(--nr-danger); background: var(--nr-danger-bg); }
}

@media (max-width: 768px) {
  .profile-page { padding-bottom: 44px; }
  .page-top-inner,
  .page-hero,
  .profile-body { width: min(100% - 28px, 860px); }
  .page-top-inner { min-height: 62px; }
  .mini-brand span { display: none; }
  .page-hero { padding: 26px 0 20px; }
  .page-hero h1 { font-size: 32px; }
  .profile-body { padding-top: 24px; }
  .card { padding: 19px 17px; }
  .overview { min-height: 0; }
  .overview .avatar { width: 62px; height: 62px; }
  .stats { gap: 8px; }
  .stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .stats .stat { min-height: 116px; }
  .stats .stat .s-num { font-size: 21px; }
  .form { grid-template-columns: 1fr; }
}
</style>
